import { useState, useEffect, useRef } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO, setHours, setMinutes, startOfDay, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import EventForm from '@/components/Calendar/EventForm';
import EventDetailsModal from '@/components/Calendar/EventDetailsModal';
import { validateTimeRange } from '@/utils/timeValidation';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

interface Event {
  id: string;
  title: string;
  description?: string;
  date: string;
  start_time: string;
  end_time: string;
  type: 'reservation' | 'event' | 'task';
  status: 'pending' | 'confirmed' | 'cancelled';
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return startOfDay(now);
  });
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [viewMode, setViewMode] = useState<'month' | 'day'>('month');
  const [selectedDayEvents, setSelectedDayEvents] = useState<Event[]>([]);
  const { profile } = useAuth();
  const dayViewRef = useRef<HTMLDivElement>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(undefined);
  const [selectedEndTime, setSelectedEndTime] = useState<string | undefined>(undefined);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const daysInMonth = eachDayOfInterval({ 
    start: calendarStart,
    end: calendarEnd 
  });
  const hours = Array.from({ length: 24 }, (_, i) => i);

  useEffect(() => {
    console.log('Calendar debug:', {
      today: format(new Date(), 'EEEE, yyyy-MM-dd'),
      currentDate: format(currentDate, 'EEEE, yyyy-MM-dd'),
      monthStart: format(monthStart, 'EEEE, yyyy-MM-dd'),
      calendarStart: format(calendarStart, 'EEEE, yyyy-MM-dd'),
      weekStartsOn: 'Monday'
    });
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const startDate = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentDate), 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('events')
        .select('*')
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) throw error;

      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSaveEvent = async (eventData: {
    title: string;
    description: string;
    date: string;
    start_time: string;
    end_time: string;
    type: 'reservation' | 'event' | 'task';
  }) => {
    // Validate time range before saving
    if (!validateTimeRange(eventData.start_time, eventData.end_time)) {
      console.error('Invalid time range');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('events')
        .insert([{
          ...eventData,
          status: 'confirmed',
          created_by: profile?.id
        }])
        .select()
        .single();

      if (error) throw error;

      setEvents(prev => [...prev, data]);
      setShowEventForm(false);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'reservation':
        return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400';
      case 'event':
        return 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400';
      case 'task':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400';
    }
  };

  const getEventDotColor = (type: string) => {
    switch (type) {
      case 'reservation':
        return 'bg-green-400 dark:bg-green-400 shadow-[0_0_8px_0px_rgba(74,222,128,0.5)] dark:shadow-[0_0_8px_0px_rgba(74,222,128,0.5)]';
      case 'event':
        return 'bg-purple-400 dark:bg-purple-400 shadow-[0_0_8px_0px_rgba(192,132,252,0.5)] dark:shadow-[0_0_8px_0px_rgba(192,132,252,0.5)]';
      case 'task':
        return 'bg-yellow-400 dark:bg-yellow-400 shadow-[0_0_8px_0px_rgba(250,204,21,0.5)] dark:shadow-[0_0_8px_0px_rgba(250,204,21,0.5)]';
      default:
        return 'bg-gray-400 dark:bg-gray-400';
    }
  };

  const handleDateClick = (date: Date) => {
    const dayEvents = events.filter(
      event => format(parseISO(event.date), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );

    if (dayEvents.length > 0) {
      setCurrentDate(date);
      setViewMode('day');
      setSelectedDayEvents(dayEvents);
    } else {
      const currentHour = new Date().getHours();
      setSelectedDate(date);
      setSelectedTime(`${currentHour.toString().padStart(2, '0')}:00`);
      setSelectedEndTime(`${(currentHour + 1).toString().padStart(2, '0')}:00`);
      setShowEventForm(true);
    }
  };

  const handleBackToMonth = () => {
    setViewMode('month');
    setSelectedDayEvents([]);
  };

  const scrollToHour = (hour: number) => {
    if (dayViewRef.current) {
      const hourHeight = 80; // height of each hour slot
      const offset = hour * hourHeight;
      dayViewRef.current.scrollTop = offset - hourHeight; // Scroll one hour above target for context
    }
  };

  useEffect(() => {
    const todayEvents = events.filter(
      event => format(parseISO(event.date), 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd')
    ).sort((a, b) => {
      const [aHour] = a.start_time.split(':').map(Number);
      const [bHour] = b.start_time.split(':').map(Number);
      return aHour - bHour;
    });

    const firstEvent = todayEvents[0];
    if (firstEvent) {
      const [hour] = firstEvent.start_time.split(':').map(Number);
      scrollToHour(hour);
    } else {
      scrollToHour(12); // Default to noon if no events
    }
  }, [currentDate, events]);

  const handlePreviousDay = () => {
    setCurrentDate(prev => startOfDay(addDays(prev, -1)));
  };

  const handleNextDay = () => {
    setCurrentDate(prev => startOfDay(addDays(prev, 1)));
  };

  const handleViewModeChange = (newMode: 'month' | 'day') => {
    setViewMode(newMode);
    // Keep the current selected date when switching views
    if (newMode === 'day' && !isSameMonth(currentDate, new Date())) {
      setCurrentDate(new Date());
    }
  };

  const handleSwipe = (swipeDirection: number) => {
    if (viewMode === 'month') {
      if (swipeDirection > 0) {
        handlePreviousMonth();
      } else {
        handleNextMonth();
      }
    } else {
      if (swipeDirection > 0) {
        handlePreviousDay();
      } else {
        handleNextDay();
      }
    }
  };

  const renderViewModeSelector = () => (
    <select
      value={viewMode}
      onChange={(e) => handleViewModeChange(e.target.value as 'month' | 'day')}
      className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-primary-500"
    >
      <option value="month">Month</option>
      <option value="day">Day</option>
    </select>
  );

  const renderDayView = () => (
    <motion.div 
      className="space-y-4 px-4 pt-4"
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragEnd={(e, { offset, velocity }) => {
        const swipe = offset.x + velocity.x * 50;
        if (Math.abs(swipe) > 50) {
          handleSwipe(swipe);
        }
      }}
    >
      <div className="space-y-4 px-4 pt-4">
        <div 
          ref={dayViewRef}
          className="bg-white dark:bg-dark-bg-light rounded-lg shadow overflow-auto max-h-[calc(100vh-12rem)]"
        >
          <div className="grid grid-cols-[80px_1fr] divide-x divide-gray-200 dark:divide-dark-border">
            {/* Time column */}
            <div className="sticky left-0 bg-white dark:bg-dark-bg-light z-10">
              {hours.map(hour => (
                <div
                  key={hour}
                  className="h-20 border-b border-gray-200 dark:border-dark-border flex items-center justify-center"
                >
                  <span className="text-sm text-gray-500 dark:text-dark-text-secondary">
                    {format(setHours(startOfDay(currentDate), hour), 'HH:mm')}
                  </span>
                </div>
              ))}
            </div>

            {/* Events column */}
            <div>
              {hours.map(hour => {
                const hourEvents = events.filter(event => {
                  const [eventHour] = event.start_time.split(':').map(Number);
                  return eventHour === hour && 
                         format(parseISO(event.date), 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd');
                });

                return (
                  <div
                    key={hour}
                    className="h-20 border-b border-gray-200 dark:border-dark-border relative group"
                    onClick={() => {
                      if (hourEvents.length === 0) {
                        setSelectedDate(currentDate);
                        setSelectedTime(`${hour.toString().padStart(2, '0')}:00`);
                        setSelectedEndTime(`${(hour + 1).toString().padStart(2, '0')}:00`);
                        setShowEventForm(true);
                      }
                    }}
                  >
                    {/* Current time indicator */}
                    {isToday(currentDate) && new Date().getHours() === hour && (
                      <div className="absolute left-0 right-0 h-0.5 bg-primary-500 z-10" style={{
                        top: `${(new Date().getMinutes() / 60) * 100}%`
                      }} />
                    )}

                    {hourEvents.map((event, index) => {
                      const overlappingEvents = hourEvents.length;
                      const eventWidth = overlappingEvents > 1 ? `${100 / overlappingEvents}%` : '95%';
                      const leftOffset = overlappingEvents > 1 ? `${(100 / overlappingEvents) * index}%` : '2.5%';

                      // Calculate event duration and position
                      const [startHour, startMinute] = event.start_time.split(':').map(Number);
                      const [endHour, endMinute] = event.end_time.split(':').map(Number);
                      
                      // Calculate top position based on start time
                      const topPercentage = (startMinute / 60) * 100;
                      
                      // Calculate height based on duration
                      const durationInMinutes = (endHour - startHour) * 60 + (endMinute - startMinute);
                      const heightPercentage = (durationInMinutes / 60) * 100;

                      return (
                        <div
                          key={event.id}
                          className={`
                            absolute p-2 rounded text-sm ${getEventColor(event.type)}
                            hover:ring-2 hover:ring-primary-500 dark:hover:ring-primary-400 cursor-pointer
                            shadow-sm
                          `}
                          style={{
                            top: `${topPercentage}%`,
                            left: leftOffset,
                            width: eventWidth,
                            height: `${heightPercentage}%`,
                            minHeight: '2rem'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event);
                          }}
                        >
                          <div className="font-medium flex justify-between items-center">
                            <span>{event.title}</span>
                            <span className="text-xs opacity-75">
                              {event.start_time.substring(0, 5)}
                            </span>
                          </div>
                          {event.description && (
                            <div className="text-xs truncate mt-1 opacity-75">
                              {event.description}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Show add button on hover only if no events */}
                    {hourEvents.length === 0 && (
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 flex items-center justify-center">
                        <div className="text-primary-500 dark:text-primary-400 text-sm">
                          <i className="fas fa-plus-circle mr-1" />
                          Add Event
                        </div>
                      </div>
                    )}

                    {/* Hover effect only for empty slots */}
                    {hourEvents.length === 0 && (
                      <div className="absolute inset-0 bg-primary-500/0 group-hover:bg-primary-500/5 transition-colors" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const handlePreviousMonth = () => {
    setCurrentDate(prev => startOfDay(new Date(prev.getFullYear(), prev.getMonth() - 1, 1)));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => startOfDay(new Date(prev.getFullYear(), prev.getMonth() + 1, 1)));
  };

  const renderMonthView = () => (
    <motion.div 
      className="space-y-4 px-4 pt-4"
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragEnd={(e, { offset, velocity }) => {
        const swipe = offset.x + velocity.x * 50;
        if (Math.abs(swipe) > 50) {
          handleSwipe(swipe);
        }
      }}
    >
      <div className="bg-white dark:bg-dark-bg-light rounded-lg shadow w-full overflow-hidden">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-px border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-xs sm:text-sm">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div
              key={day}
              className="py-2 sm:py-3 text-center font-medium text-gray-500 dark:text-dark-text-secondary"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-dark-border">
          {daysInMonth.map(day => {
            const dayEvents = events.filter(
              event => format(parseISO(event.date), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')
            );

            const isCurrentMonth = isSameMonth(day, currentDate);
            const isCurrentDay = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

            return (
              <div
                key={day.toString()}
                onClick={() => handleDateClick(day)}
                className={`
                  min-h-[6rem] sm:min-h-32 bg-white dark:bg-dark-bg-light p-2 cursor-pointer
                  transition-colors duration-200
                  hover:bg-gray-50 dark:hover:bg-dark-bg
                  ${!isCurrentMonth && 'bg-gray-50/50 dark:bg-dark-bg/50 text-gray-400 dark:text-dark-text-secondary'}
                  ${isCurrentDay && 'bg-blue-50 dark:bg-blue-900/20'}
                  ${isCurrentMonth && !isCurrentDay && 'hover:bg-primary-50 dark:hover:bg-primary-900/10'}
                `}
                title={format(day, 'EEEE, MMMM d, yyyy')}
              >
                <div className="flex justify-between">
                  <span className={`
                    text-sm font-medium rounded-full w-6 h-6 flex items-center justify-center
                    ${isCurrentDay ? 'bg-primary-500 text-white' : ''}
                    ${!isCurrentDay && isCurrentMonth ? 'hover:bg-primary-100 dark:hover:bg-primary-900/20' : ''}
                  `}>
                    {format(day, 'd')}
                  </span>
                  {dayEvents.length > 0 && (
                    <span className="text-xs text-primary-600 dark:text-primary-400 font-medium sm:hidden">
                      {dayEvents.length}
                    </span>
                  )}
                </div>
                
                {/* Event indicators */}
                <div className="mt-1 space-y-1 max-h-20 sm:max-h-24 overflow-hidden">
                  {/* Mobile view - dots */}
                  <div className="flex gap-1 flex-wrap sm:hidden mt-1">
                    {dayEvents.map(event => (
                      <div
                        key={event.id}
                        className={`w-2 h-2 rounded-full ${getEventDotColor(event.type)}`}
                      />
                    ))}
                  </div>

                  {/* Desktop view - event details */}
                  <div className="hidden sm:block">
                    {dayEvents.slice(0, 3).map(event => (
                      <div
                        key={event.id}
                        className={`
                          text-xs truncate rounded px-1.5 py-0.5
                          ${getEventColor(event.type)}
                        `}
                      >
                        <span className="mr-1.5">{event.start_time.substring(0, 5)}</span>
                        {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-gray-500 dark:text-dark-text-secondary px-1.5">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
  };

  const handleUpdateEvent = async (updatedEvent: Event) => {
    // Validate time range before updating
    if (!validateTimeRange(updatedEvent.start_time, updatedEvent.end_time)) {
      console.error('Invalid time range');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('events')
        .update({
          title: updatedEvent.title,
          description: updatedEvent.description,
          start_time: updatedEvent.start_time,
          end_time: updatedEvent.end_time,
          type: updatedEvent.type,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedEvent.id)
        .select()
        .single();

      if (error) throw error;

      setEvents(prev => prev.map(event => 
        event.id === updatedEvent.id ? data : event
      ));
      await fetchEvents();
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      setEvents(prev => prev.filter(event => event.id !== eventId));
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  };

  const resetForm = () => {
    setShowEventForm(false);
    setSelectedTime(undefined);
    setSelectedEndTime(undefined);
  };

  // Add debug logging
  useEffect(() => {
    console.log('Calendar dates:', {
      currentDate: format(currentDate, 'EEEE, yyyy-MM-dd'),
      monthStart: format(monthStart, 'EEEE, yyyy-MM-dd'),
      calendarStart: format(calendarStart, 'EEEE, yyyy-MM-dd'),
      isToday: isToday(currentDate),
      weekday: format(currentDate, 'EEEE')
    });
  }, [currentDate, monthStart, calendarStart]);

  // Update the isToday check to be more precise
  const isTodayPrecise = (date: Date) => {
    const today = new Date();
    return format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
  };

  return (
    <div className="space-y-4 px-2 pt-4">
      {/* Date Navigation Header */}
      <div className="bg-white dark:bg-dark-bg-light rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {viewMode === 'month' ? 'Calendar' : 'Daily Schedule'}
          </h2>
          <div className="flex items-center">
            <div className="flex items-center space-x-2 text-base">
              <select
                value={viewMode}
                onChange={(e) => handleViewModeChange(e.target.value as 'month' | 'day')}
                className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-primary-500"
              >
                <option value="month">Month</option>
                <option value="day">Day</option>
              </select>
              <div className="flex items-center space-x-2">
                <button
                  onClick={viewMode === 'month' ? handlePreviousMonth : handlePreviousDay}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-lighter"
                >
                  <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-dark-text-secondary" />
                </button>
                <span className="text-gray-900 dark:text-dark-text-primary font-medium">
                  {viewMode === 'month'
                    ? format(currentDate, 'MMM yyyy')
                    : format(currentDate, 'MMM d, yyyy')}
                </span>
                <button
                  onClick={viewMode === 'month' ? handleNextMonth : handleNextDay}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-lighter"
                >
                  <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-dark-text-secondary" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'month' ? renderMonthView() : renderDayView()}

      {showEventForm && selectedDate && (
        <EventForm
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          selectedEndTime={selectedEndTime}
          onClose={resetForm}
          onSave={async (eventData) => {
            await handleSaveEvent(eventData);
            resetForm();
          }}
        />
      )}

      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onEdit={handleUpdateEvent}
          onDelete={handleDeleteEvent}
        />
      )}
    </div>
  );
} 